import React, { useEffect, useMemo, useRef, useState } from 'react';
import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isBefore, isSameMonth, parseISO, startOfDay, startOfMonth, startOfWeek } from 'date-fns';
import { fr, enCA } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import AccessibleModal from '../components/common/AccessibleModal';
import { activitiesAPI, centersAPI, monthlyProgramsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import i18n from '../i18n';
import brandLogo from '../assets/logo-centre-jour.png';

const DEFAULT_DAY = { note: '', sections: [] };
const WEEKDAY_KEYS = [1, 2, 3, 4, 5];
const DEFAULT_CENTER_DISPLAY = 'Centre de Jour Vivre en Sante';
const DEFAULT_FOOTER = 'Votre satisfaction et le Bien-etre de vos proches sont nos priorites absolues';
const DEFAULT_PROGRAM_SUBTITLE = 'Soutien aux adultes atteints de perte de memoire et a leurs familles';
const TIME_OPTIONS = [
  '',
  '08:00',
  '08:30',
  '09:00',
  '09:30',
  '10:00',
  '10:30',
  '11:00',
  '11:30',
  '12:00',
  '12:30',
  '13:00',
  '13:30',
  '14:00',
  '14:30',
  '15:00',
  '15:30',
  '16:00',
  '16:30',
];

const createEmptyEntry = () => ({ time: '', text: '' });
const createEmptySection = () => ({ title: '', entries: [createEmptyEntry()] });
const createMonthlyProgramPayload = (programValue, centerId, monthDate) => ({
  centerId,
  month: formatMonthValue(monthDate),
  title: programValue?.title || '',
  subtitle: programValue?.subtitle || '',
  heroTitle: programValue?.heroTitle || '',
  heroDescription: programValue?.heroDescription || '',
  seasonTitle: programValue?.seasonTitle || '',
  seasonHeading: programValue?.seasonHeading || '',
  seasonDescription: programValue?.seasonDescription || '',
  footerNote: programValue?.footerNote || '',
  accentColor: programValue?.accentColor || '#2168ad',
  days: programValue?.days || {},
});

const remapDaysToMonth = (days, sourceMonth, targetMonth) => {
  const remapped = {};
  const targetYear = targetMonth.getFullYear();
  const targetMonthIndex = targetMonth.getMonth();
  const maxDay = endOfMonth(targetMonth).getDate();

  Object.entries(days || {}).forEach(([dateKey, value]) => {
    const sourceDate = parseISO(dateKey);
    if (!isSameMonth(sourceDate, sourceMonth)) return;
    const dayOfMonth = sourceDate.getDate();
    if (dayOfMonth > maxDay) return;
    const targetDate = new Date(targetYear, targetMonthIndex, dayOfMonth);
    remapped[getDateKey(targetDate)] = JSON.parse(JSON.stringify(value));
  });

  return remapped;
};

const normalizeDay = (day) => ({
  note: day?.note || '',
  sections: Array.isArray(day?.sections) && day.sections.length
    ? day.sections.map((section) => ({
      title: section?.title || '',
      entries: Array.isArray(section?.entries) && section.entries.length
        ? section.entries.map((entry) => ({ time: entry?.time || '', text: entry?.text || '' }))
        : [createEmptyEntry()],
    }))
    : [
      {
        title: day?.headline || '',
        entries: Array.isArray(day?.entries) && day.entries.length
          ? day.entries.map((entry) => ({ time: entry?.time || '', text: entry?.text || '' }))
          : [createEmptyEntry()],
      },
    ],
});

const formatMonthValue = (date) => format(date, 'yyyy-MM');
const getDateKey = (date) => format(date, 'yyyy-MM-dd');
const weekdayLabel = (weekday, locale) => format(new Date(2026, 0, 4 + weekday), 'EEEE', { locale });

const getMonthTheme = (date, locale) => {
  const monthIndex = date.getMonth();
  const monthYearLabel = `${format(date, 'MMMM', { locale })} ${format(date, 'yyyy')}`;

  if (monthIndex === 3) {
    return {
      title: `${monthYearLabel} • Paques`,
      heading: DEFAULT_PROGRAM_SUBTITLE,
      caption: 'Une ambiance douce et printaniere pour celebrer le mois de Paques.',
      variant: 'easter',
    };
  }

  return {
    title: monthYearLabel,
    heading: DEFAULT_PROGRAM_SUBTITLE,
    caption: 'Visuel du mois a personnaliser selon vos evenements et moments forts.',
    variant: 'seasonal',
  };
};

const DayEditorModal = ({ date, value, onClose, onSave }) => {
  const [localValue, setLocalValue] = useState(() => normalizeDay(value));
  const locale = i18n.language.startsWith('fr') ? fr : enCA;

  const updateSectionTitle = (sectionIndex, title) => {
    setLocalValue((current) => ({
      ...current,
      sections: current.sections.map((section, index) => (
        index === sectionIndex ? { ...section, title } : section
      )),
    }));
  };

  const updateEntry = (sectionIndex, entryIndex, field, fieldValue) => {
    setLocalValue((current) => ({
      ...current,
      sections: current.sections.map((section, index) => (
        index === sectionIndex
          ? {
            ...section,
            entries: section.entries.map((entry, currentEntryIndex) => (
              currentEntryIndex === entryIndex ? { ...entry, [field]: fieldValue } : entry
            )),
          }
          : section
      )),
    }));
  };

  const addEntry = (sectionIndex) => {
    setLocalValue((current) => ({
      ...current,
      sections: current.sections.map((section, index) => (
        index === sectionIndex ? { ...section, entries: [...section.entries, createEmptyEntry()] } : section
      )),
    }));
  };

  const removeEntry = (sectionIndex, entryIndex) => {
    setLocalValue((current) => ({
      ...current,
      sections: current.sections.map((section, index) => {
        if (index !== sectionIndex) return section;
        return {
          ...section,
          entries: section.entries.length === 1
            ? [createEmptyEntry()]
            : section.entries.filter((_, currentEntryIndex) => currentEntryIndex !== entryIndex),
        };
      }),
    }));
  };

  const addSection = () => {
    setLocalValue((current) => ({
      ...current,
      sections: [...current.sections, createEmptySection()],
    }));
  };

  const removeSection = (sectionIndex) => {
    setLocalValue((current) => ({
      ...current,
      sections: current.sections.length === 1
        ? [createEmptySection()]
        : current.sections.filter((_, index) => index !== sectionIndex),
    }));
  };

  const handleSave = (mode = 'single') => {
    const cleaned = {
      note: localValue.note.trim(),
      sections: localValue.sections
        .map((section) => ({
          title: section.title.trim(),
          entries: section.entries
            .map((entry) => ({ time: entry.time.trim(), text: entry.text.trim() }))
            .filter((entry) => entry.time || entry.text),
        }))
        .filter((section) => section.title || section.entries.length),
    };
    onSave(cleaned, mode);
  };

  return (
    <AccessibleModal
      title={`Programme du ${format(date, 'EEEE d MMMM yyyy', { locale: i18n.language.startsWith('fr') ? fr : enCA })}`}
      onClose={onClose}
      maxWidth="860px"
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Fermer</button>
          <button type="button" className="btn btn-outline" onClick={() => handleSave('same-weekday')}>
            Appliquer a tous les {format(date, 'EEEE', { locale })}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => handleSave('single')}>Appliquer</button>
        </>
      )}
    >
      <div className="form-group">
        <label className="form-label">Note additionnelle</label>
        <textarea
          className="form-textarea"
          rows={3}
          value={localValue.note}
          onChange={(event) => setLocalValue((current) => ({ ...current, note: event.target.value }))}
          placeholder="Message pour les familles, menu special, rappel, etc."
        />
      </div>

      <div className="form-group">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <label className="form-label" style={{ marginBottom: 0 }}>Blocs de la journee</label>
          <button type="button" className="btn btn-outline btn-sm" onClick={addSection}>+ Ajouter un titre special</button>
        </div>
        <div style={{ display: 'grid', gap: 16 }}>
          {localValue.sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="monthly-program-section-editor">
              <div className="monthly-program-section-header">
                <input
                  className="form-input"
                  value={section.title}
                  onChange={(event) => updateSectionTitle(sectionIndex, event.target.value)}
                  placeholder="Ex. Restaurant ou Activites"
                />
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeSection(sectionIndex)}>Retirer ce bloc</button>
              </div>
              <div style={{ display: 'grid', gap: 10 }}>
                {section.entries.map((entry, entryIndex) => (
                  <div key={entryIndex} className="monthly-program-entry-editor">
                    <select
                      className="form-select"
                      value={entry.time}
                      onChange={(event) => updateEntry(sectionIndex, entryIndex, 'time', event.target.value)}
                    >
                      {TIME_OPTIONS.map((option) => (
                        <option key={option || 'empty'} value={option}>
                          {option || 'Heure'}
                        </option>
                      ))}
                    </select>
                    <input
                      className="form-input"
                      value={entry.text}
                      onChange={(event) => updateEntry(sectionIndex, entryIndex, 'text', event.target.value)}
                      placeholder="Ex. Diner"
                    />
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeEntry(sectionIndex, entryIndex)}>Retirer</button>
                  </div>
                ))}
              </div>
              <button type="button" className="btn btn-outline btn-sm" style={{ marginTop: 10 }} onClick={() => addEntry(sectionIndex)}>
                + Ajouter une heure
              </button>
            </div>
          ))}
        </div>
      </div>
    </AccessibleModal>
  );
};

const MonthlyProgramPage = () => {
  const { t } = useTranslation();
  const { user, hasRole } = useAuth();
  const locale = i18n.language.startsWith('fr') ? fr : enCA;
  const [centers, setCenters] = useState([]);
  const [selectedCenterId, setSelectedCenterId] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => startOfMonth(new Date()));
  const [program, setProgram] = useState(null);
  const [programPreviews, setProgramPreviews] = useState({});
  const [scheduledActivities, setScheduledActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editorDate, setEditorDate] = useState(null);
  const [copyTargetCenterId, setCopyTargetCenterId] = useState('');
  const [copyTargetMonth, setCopyTargetMonth] = useState(() => startOfMonth(new Date()));
  const [copyMode, setCopyMode] = useState('month');
  const [copySourceDay, setCopySourceDay] = useState('');
  const [copyTargetDay, setCopyTargetDay] = useState('');
  const [copyFeedback, setCopyFeedback] = useState('');
  const editorSectionRef = useRef(null);

  const canEdit = hasRole('admin', 'manager');

  const visibleCenters = useMemo(() => {
    if (user?.role === 'admin') return centers;
    const allowed = new Set([...(user?.center_ids || []), user?.center_id].filter(Boolean));
    return centers.filter((center) => allowed.has(center.id));
  }, [centers, user]);

  const copyDestinationCenters = useMemo(
    () => visibleCenters.filter((center) => center.id !== selectedCenterId),
    [selectedCenterId, visibleCenters]
  );

  useEffect(() => {
    const loadCenters = async () => {
      try {
        const response = await centersAPI.getAll();
        setCenters(response.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    loadCenters();
  }, []);

  useEffect(() => {
    if (!visibleCenters.length || selectedCenterId) return;
    setSelectedCenterId(visibleCenters[0].id);
  }, [visibleCenters, selectedCenterId]);

  useEffect(() => {
    if (!copyDestinationCenters.length) return;
    if (copyTargetCenterId && copyDestinationCenters.some((center) => center.id === copyTargetCenterId)) return;
    const fallbackCenter = copyDestinationCenters[0];
    setCopyTargetCenterId(fallbackCenter?.id || '');
  }, [copyDestinationCenters, copyTargetCenterId]);

  useEffect(() => {
    if (!selectedCenterId) return;

    const loadProgram = async () => {
      setLoading(true);
      setError('');
      try {
        const [programResponse, activitiesResponse] = await Promise.all([
          monthlyProgramsAPI.get({
            centerId: selectedCenterId,
            month: formatMonthValue(selectedMonth),
          }),
          activitiesAPI.getAll({
            centerId: selectedCenterId,
            dateFrom: startOfMonth(selectedMonth).toISOString(),
            dateTo: endOfMonth(selectedMonth).toISOString(),
            status: 'scheduled',
          }),
        ]);
        setProgram({
          ...programResponse.data,
          days: programResponse.data?.days || {},
          accentColor: programResponse.data?.accentColor || '#2168ad',
        });
        setScheduledActivities(activitiesResponse.data || []);
      } catch (err) {
        console.error(err);
        setScheduledActivities([]);
        setError(err.response?.data?.message || 'Impossible de charger le programme mensuel.');
      } finally {
        setLoading(false);
      }
    };

    loadProgram();
  }, [selectedCenterId, selectedMonth]);

  useEffect(() => {
    if (!visibleCenters.length) return;

    const loadPreviews = async () => {
      try {
        const responses = await Promise.all(
          visibleCenters.map((center) => monthlyProgramsAPI.get({
            centerId: center.id,
            month: formatMonthValue(selectedMonth),
          }))
        );

        const nextPreviews = {};
        visibleCenters.forEach((center, index) => {
          const responseData = responses[index]?.data || {};
          nextPreviews[center.id] = {
            ...responseData,
            center,
            days: responseData?.days || {},
            accentColor: responseData?.accentColor || '#2168ad',
          };
        });
        setProgramPreviews(nextPreviews);
      } catch (err) {
        console.error(err);
        setProgramPreviews({});
      }
    };

    loadPreviews();
  }, [visibleCenters, selectedMonth]);

  const weeks = useMemo(() => {
    const monthStart = startOfMonth(selectedMonth);
    const monthEnd = endOfMonth(selectedMonth);
    const range = eachDayOfInterval({
      start: startOfWeek(monthStart, { weekStartsOn: 1 }),
      end: endOfWeek(monthEnd, { weekStartsOn: 1 }),
    });

    const weekdays = range.filter((date) => {
      const day = date.getDay();
      return day >= 1 && day <= 5;
    });

    const rows = [];
    for (let index = 0; index < weekdays.length; index += 5) rows.push(weekdays.slice(index, index + 5));
    return rows;
  }, [selectedMonth]);

  const monthDayOptions = useMemo(() => (
    weeks
      .flat()
      .filter((date) => isSameMonth(date, selectedMonth))
      .map((date) => ({
        value: getDateKey(date),
        label: format(date, 'EEEE d MMMM', { locale }),
      }))
  ), [locale, selectedMonth, weeks]);

  const targetMonthDayOptions = useMemo(() => {
    const targetMonthStart = startOfMonth(copyTargetMonth);
    const targetMonthEnd = endOfMonth(copyTargetMonth);
    const range = eachDayOfInterval({
      start: startOfWeek(targetMonthStart, { weekStartsOn: 1 }),
      end: endOfWeek(targetMonthEnd, { weekStartsOn: 1 }),
    });

    return range
      .filter((date) => {
        const day = date.getDay();
        return isSameMonth(date, copyTargetMonth) && day >= 1 && day <= 5;
      })
      .map((date) => ({
        value: getDateKey(date),
        label: format(date, 'EEEE d MMMM', { locale }),
      }));
  }, [copyTargetMonth, locale]);

  useEffect(() => {
    if (!monthDayOptions.length) {
      setCopySourceDay('');
      return;
    }
    if (!copySourceDay || !monthDayOptions.some((option) => option.value === copySourceDay)) {
      setCopySourceDay(monthDayOptions[0].value);
    }
  }, [copySourceDay, monthDayOptions]);

  useEffect(() => {
    if (!targetMonthDayOptions.length) {
      setCopyTargetDay('');
      return;
    }
    if (!copyTargetDay || !targetMonthDayOptions.some((option) => option.value === copyTargetDay)) {
      setCopyTargetDay(targetMonthDayOptions[0].value);
    }
  }, [copyTargetDay, targetMonthDayOptions]);

  const scheduledActivitiesByDay = useMemo(() => (
    scheduledActivities.reduce((accumulator, activity) => {
      if (!activity?.starts_at) return accumulator;
      const dateKey = getDateKey(new Date(activity.starts_at));
      if (!accumulator[dateKey]) accumulator[dateKey] = [];
      accumulator[dateKey].push(activity);
      return accumulator;
    }, {})
  ), [scheduledActivities]);

  const previewCards = useMemo(() => (
    visibleCenters.map((center) => {
      const preview = programPreviews[center.id] || {};
      const previewMonthLabel = format(selectedMonth, 'MMMM', { locale });
      const previewDaysCount = Object.keys(preview.days || {}).length;
      return {
        center,
        title: center.name || DEFAULT_CENTER_DISPLAY,
        address: center.address || '',
        subtitle: preview.subtitle || 'Calendrier du centre',
        monthLabel: `${previewMonthLabel} ${format(selectedMonth, 'yyyy')}`,
        accentColor: preview.accentColor || '#2168ad',
        daysCount: previewDaysCount,
      };
    })
  ), [locale, programPreviews, selectedMonth, visibleCenters]);

  const openCenterCalendar = (centerId) => {
    setSelectedCenterId(centerId);
    window.setTimeout(() => {
      editorSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
  };

  const handleCopyToCenter = async () => {
    if (!program || !copyTargetCenterId) return;

    setSaving(true);
    setError('');
    setCopyFeedback('');

    try {
      const targetMonthValue = formatMonthValue(copyTargetMonth);
      const targetResponse = await monthlyProgramsAPI.get({
        centerId: copyTargetCenterId,
        month: targetMonthValue,
      });

      const targetCenter = visibleCenters.find((center) => center.id === copyTargetCenterId) || null;
      const targetProgram = {
        ...targetResponse.data,
        days: targetResponse.data?.days || {},
        accentColor: targetResponse.data?.accentColor || '#2168ad',
      };

      let programToSave;

      if (copyMode === 'day') {
        const sourceDayData = program.days?.[copySourceDay];
        if (!sourceDayData) {
          throw new Error('La journee source est vide. Rien a copier.');
        }
        programToSave = {
          ...targetProgram,
          days: {
            ...(targetProgram.days || {}),
            [copyTargetDay]: JSON.parse(JSON.stringify(sourceDayData)),
          },
        };
      } else {
        programToSave = {
          ...targetProgram,
          title: targetCenter?.name || targetProgram.center?.name || '',
          subtitle: program.subtitle || targetProgram.subtitle,
          heroTitle: program.heroTitle || targetProgram.heroTitle,
          heroDescription: program.heroDescription || targetProgram.heroDescription,
          seasonTitle: program.seasonTitle || targetProgram.seasonTitle,
          seasonHeading: program.seasonHeading || targetProgram.seasonHeading,
          seasonDescription: program.seasonDescription || targetProgram.seasonDescription,
          footerNote: program.footerNote || targetProgram.footerNote,
          accentColor: program.accentColor || targetProgram.accentColor,
          days: remapDaysToMonth(program.days || {}, selectedMonth, copyTargetMonth),
        };
      }

      const saveResponse = await monthlyProgramsAPI.save(
        createMonthlyProgramPayload(programToSave, copyTargetCenterId, copyTargetMonth)
      );
      const savedProgram = {
        ...saveResponse.data,
        days: saveResponse.data?.days || {},
        accentColor: saveResponse.data?.accentColor || '#2168ad',
      };

      setProgramPreviews((current) => ({
        ...current,
        [copyTargetCenterId]: {
          ...savedProgram,
          center: targetCenter || current[copyTargetCenterId]?.center || null,
        },
      }));

      setSelectedCenterId(copyTargetCenterId);
      setSelectedMonth(startOfMonth(copyTargetMonth));
      setProgram(null);
      window.setTimeout(() => {
        editorSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);

      setCopyFeedback(copyMode === 'day'
        ? 'La journee a ete copiee vers le centre cible.'
        : 'Le mois complet a ete copie vers le centre cible.');
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || err.message || 'Impossible de copier vers cet autre centre.');
    } finally {
      setSaving(false);
    }
  };

  const updateProgram = (field, value) => {
    setProgram((current) => ({ ...current, [field]: value }));
  };

  const openDayEditor = (date) => setEditorDate(date);

  const saveDay = async (value, mode = 'single') => {
    const dayKey = getDateKey(editorDate);
    const nextProgram = (() => {
      const nextDays = { ...(program?.days || {}) };
      const hasSections = Array.isArray(value.sections) && value.sections.some(
        (section) => section.title || (Array.isArray(section.entries) && section.entries.length)
      );
      const shouldDelete = !value.note && !hasSections;

      if (mode === 'same-weekday') {
        weeks.flat().forEach((date) => {
          if (!isSameMonth(date, selectedMonth)) return;
          if (date.getDay() !== editorDate.getDay()) return;
          const key = getDateKey(date);
          if (shouldDelete) delete nextDays[key];
          else nextDays[key] = JSON.parse(JSON.stringify(value));
        });
      } else if (shouldDelete) {
        delete nextDays[dayKey];
      } else {
        nextDays[dayKey] = value;
      }

      return { ...program, days: nextDays };
    })();

    setProgram(nextProgram);
    setEditorDate(null);

    setSaving(true);
    setError('');
    try {
      await persistProgram(nextProgram);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Impossible d enregistrer le programme.');
    } finally {
      setSaving(false);
    }
  };

  const copyFromPreviousWeek = () => {
    setProgram((current) => {
      const nextDays = { ...(current?.days || {}) };
      weeks.flat().forEach((date) => {
        if (!isSameMonth(date, selectedMonth)) return;
        const previousWeekDate = new Date(date);
        previousWeekDate.setDate(date.getDate() - 7);
        const sourceKey = getDateKey(previousWeekDate);
        const targetKey = getDateKey(date);
        if (nextDays[sourceKey] && !nextDays[targetKey]) {
          nextDays[targetKey] = JSON.parse(JSON.stringify(nextDays[sourceKey]));
        }
      });
      return { ...current, days: nextDays };
    });
  };

  const clearMonth = () => {
    setProgram((current) => ({ ...current, days: {} }));
  };

  const copyWeekForward = (weekIndex) => {
    setProgram((current) => {
      const nextDays = { ...(current?.days || {}) };
      const sourceWeek = weeks[weekIndex] || [];

      sourceWeek.forEach((date) => {
        if (!isSameMonth(date, selectedMonth)) return;
        const sourceKey = getDateKey(date);
        const targetDate = new Date(date);
        targetDate.setDate(date.getDate() + 7);
        if (!isSameMonth(targetDate, selectedMonth)) return;
        const targetKey = getDateKey(targetDate);
        if (!nextDays[sourceKey]) return;
        nextDays[targetKey] = JSON.parse(JSON.stringify(nextDays[sourceKey]));
      });

      return { ...current, days: nextDays };
    });
  };

  const persistProgram = async (programToSave) => {
    const response = await monthlyProgramsAPI.save({
      centerId: selectedCenterId,
      month: formatMonthValue(selectedMonth),
      title: programToSave?.title || '',
      subtitle: programToSave?.subtitle || '',
      heroTitle: programToSave?.heroTitle || '',
      heroDescription: programToSave?.heroDescription || '',
      seasonTitle: programToSave?.seasonTitle || '',
      seasonHeading: programToSave?.seasonHeading || '',
      seasonDescription: programToSave?.seasonDescription || '',
      footerNote: programToSave?.footerNote || '',
      accentColor: programToSave?.accentColor || '#2168ad',
      days: programToSave?.days || {},
    });
    setProgram({ ...response.data, days: response.data?.days || {} });
    return response.data;
  };

  const handleSaveProgram = async () => {
    setSaving(true);
    setError('');
    try {
      await persistProgram(program);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Impossible d enregistrer le programme.');
    } finally {
      setSaving(false);
    }
  };

  const selectedCenter = visibleCenters.find((center) => center.id === selectedCenterId) || program?.center;
  const logoSource = selectedCenter?.logo_url || brandLogo;
  const monthLabel = format(selectedMonth, 'MMMM', { locale });
  const displayCenterName = selectedCenter?.name || program?.center?.name || DEFAULT_CENTER_DISPLAY;
  const displayScheduleTitle = `Programme: ${monthLabel} - ${format(selectedMonth, 'yyyy')}`;
  const displayFooter = program?.footerNote || DEFAULT_FOOTER;
  const displayProgramSubtitle = DEFAULT_PROGRAM_SUBTITLE;
  const monthTheme = getMonthTheme(selectedMonth, locale);
  const displaySeasonTitle = program?.seasonTitle || monthTheme.title;
  const displaySeasonHeading = program?.seasonHeading || monthTheme.heading;
  const displaySeasonDescription = program?.seasonDescription || monthTheme.caption;
  const today = startOfDay(new Date());

  return (
    <div className="monthly-program-page">
      <div className="page-header monthly-program-toolbar">
        <div>
          <h2>Calendrier mensuel</h2>
          <p className="text-muted">
            Programme imprimable de type affiche pour les activites, repas et temps forts du centre.
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-outline" onClick={() => setSelectedMonth((current) => addMonths(current, -1))}>← Mois precedent</button>
          <button className="btn btn-outline" onClick={() => setSelectedMonth(startOfMonth(new Date()))}>Mois courant</button>
          <button className="btn btn-outline" onClick={() => setSelectedMonth((current) => addMonths(current, 1))}>Mois suivant →</button>
          <button className="btn btn-secondary" type="button" onClick={() => window.print()}>Imprimer / PDF</button>
          {canEdit && (
            <button className="btn btn-primary" type="button" onClick={handleSaveProgram} disabled={saving || !program}>
              {saving ? t('common.loading') : 'Enregistrer'}
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error monthly-program-toolbar" role="alert">⚠ {error}</div>}

      <div className="monthly-program-gallery monthly-program-toolbar">
        <div className="monthly-program-gallery-head">
          <div>
            <h3>Calendriers par centre</h3>
            <p className="text-muted">Choisis un centre en miniature, puis ouvre son calendrier pour le modifier.</p>
          </div>
        </div>
        <div className="monthly-program-gallery-grid">
          {previewCards.map((card) => {
            const isActive = card.center.id === selectedCenterId;
            return (
              <button
                key={card.center.id}
                type="button"
                className={`monthly-program-gallery-card${isActive ? ' is-active' : ''}`}
                onClick={() => openCenterCalendar(card.center.id)}
                style={{ '--preview-accent': card.accentColor }}
              >
                <div className="monthly-program-gallery-poster">
                  <div className="monthly-program-gallery-poster-header">
                    <strong>{card.title}</strong>
                    {card.address ? <small>{card.address}</small> : null}
                    <span>{card.monthLabel}</span>
                  </div>
                  <div className="monthly-program-gallery-poster-grid" aria-hidden="true">
                    {Array.from({ length: 25 }).map((_, index) => (
                      <span key={`${card.center.id}-mini-${index}`} className={index < Math.min(card.daysCount, 25) ? 'is-filled' : ''} />
                    ))}
                  </div>
                  <div className="monthly-program-gallery-poster-footer">
                    <span>{card.daysCount} jour{card.daysCount > 1 ? 's' : ''} rempli{card.daysCount > 1 ? 's' : ''}</span>
                    <span>{isActive ? 'Ouvert' : 'Ouvrir'}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div ref={editorSectionRef} className="monthly-program-controls monthly-program-toolbar">
        <div className="card card-form">
          <div className="monthly-program-controls-grid">
            <div className="form-group">
              <label className="form-label">Centre</label>
              <select
                className="form-select"
                value={selectedCenterId}
                onChange={(event) => setSelectedCenterId(event.target.value)}
                disabled={!visibleCenters.length}
              >
                {visibleCenters.map((center) => <option key={center.id} value={center.id}>{center.name}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Mois</label>
              <input
                className="form-input"
                type="month"
                value={formatMonthValue(selectedMonth)}
                onChange={(event) => {
                  if (!event.target.value) return;
                  setSelectedMonth(parseISO(`${event.target.value}-01`));
                }}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Couleur principale</label>
              <input
                className="form-input"
                type="color"
                value={program?.accentColor || '#2168ad'}
                onChange={(event) => updateProgram('accentColor', event.target.value)}
                disabled={!program}
              />
            </div>

            {canEdit && (
              <div className="form-group monthly-program-copy-action">
                <label className="form-label">Remplissage rapide</label>
                <div className="monthly-program-quick-actions">
                  <button type="button" className="btn btn-outline" onClick={copyFromPreviousWeek} disabled={!program}>
                    Copier la semaine precedente
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={clearMonth} disabled={!program}>
                    Vider le mois
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {canEdit && program && copyDestinationCenters.length > 0 && (
          <div className="card card-form">
            <h3>Dupliquer vers un autre centre</h3>
            <div className="monthly-program-controls-grid monthly-program-copy-grid">
              <div className="form-group">
                <label className="form-label">Mode de copie</label>
                <select className="form-select" value={copyMode} onChange={(event) => setCopyMode(event.target.value)}>
                  <option value="month">Mois complet</option>
                  <option value="day">Une journee</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Centre destination</label>
                <select className="form-select" value={copyTargetCenterId} onChange={(event) => setCopyTargetCenterId(event.target.value)}>
                  {copyDestinationCenters.map((center) => (
                    <option key={center.id} value={center.id}>
                      {center.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Mois destination</label>
                <input
                  className="form-input"
                  type="month"
                  value={formatMonthValue(copyTargetMonth)}
                  onChange={(event) => {
                    if (!event.target.value) return;
                    setCopyTargetMonth(parseISO(`${event.target.value}-01`));
                  }}
                />
              </div>

              {copyMode === 'day' ? (
                <>
                  <div className="form-group">
                    <label className="form-label">Jour source</label>
                    <select className="form-select" value={copySourceDay} onChange={(event) => setCopySourceDay(event.target.value)}>
                      {monthDayOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Jour destination</label>
                    <select className="form-select" value={copyTargetDay} onChange={(event) => setCopyTargetDay(event.target.value)}>
                      {targetMonthDayOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                <div className="form-group monthly-program-copy-note">
                  <label className="form-label">Contenu copie</label>
                  <div className="text-muted">Le mois complet copie le contenu manuel et les textes de l affiche vers le centre destination.</div>
                </div>
              )}

              <div className="form-group monthly-program-copy-action">
                <label className="form-label">Action</label>
                <button type="button" className="btn btn-primary" onClick={handleCopyToCenter} disabled={saving || !copyTargetCenterId}>
                  {copyMode === 'day' ? 'Copier la journee' : 'Copier le mois'}
                </button>
              </div>
            </div>
            {copyFeedback ? <div className="alert alert-success" style={{ marginTop: 12 }}>{copyFeedback}</div> : null}
          </div>
        )}

        {canEdit && program && (
          <div className="card card-form">
            <div className="monthly-program-controls-grid monthly-program-meta-grid">
              <div className="form-group">
                <label className="form-label">Nom affiche du centre</label>
                <input className="form-input" value={program.title || ''} onChange={(event) => updateProgram('title', event.target.value)} placeholder={DEFAULT_CENTER_DISPLAY} />
              </div>
              <div className="form-group">
                <label className="form-label">Phrase descriptive</label>
                <input className="form-input" value={program.subtitle || ''} onChange={(event) => updateProgram('subtitle', event.target.value)} placeholder="Optionnel" />
              </div>
              <div className="form-group">
                <label className="form-label">Titre decoratif / fete du mois</label>
                <input className="form-input" value={program.heroTitle || ''} onChange={(event) => updateProgram('heroTitle', event.target.value)} placeholder="Ex. Joyeuses Paques" />
              </div>
              <div className="form-group">
                <label className="form-label">Pied de page</label>
                <input className="form-input" value={program.footerNote || ''} onChange={(event) => updateProgram('footerNote', event.target.value)} placeholder={DEFAULT_FOOTER} />
              </div>
              <div className="form-group monthly-program-span-full">
                <label className="form-label">Description du bloc vedette</label>
                <textarea className="form-textarea" rows={3} value={program.heroDescription || ''} onChange={(event) => updateProgram('heroDescription', event.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Titre du bloc saisonnier</label>
                <input className="form-input" value={program.seasonTitle || ''} onChange={(event) => updateProgram('seasonTitle', event.target.value)} placeholder={monthTheme.title} />
              </div>
              <div className="form-group">
                <label className="form-label">Grand texte du bloc saisonnier</label>
                <textarea className="form-textarea" rows={3} value={program.seasonHeading || ''} onChange={(event) => updateProgram('seasonHeading', event.target.value)} placeholder={monthTheme.heading} />
              </div>
              <div className="form-group monthly-program-span-full">
                <label className="form-label">Texte descriptif du bloc saisonnier</label>
                <textarea className="form-textarea" rows={3} value={program.seasonDescription || ''} onChange={(event) => updateProgram('seasonDescription', event.target.value)} placeholder={monthTheme.caption} />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="monthly-program-preview-shell">
        {loading || !program ? (
          <div className="card">
            <div style={{ padding: 48, textAlign: 'center' }}>
              <div className="spinner" style={{ margin: '0 auto 12px' }} />
              Chargement du calendrier...
            </div>
          </div>
        ) : (
          <div className="monthly-program-paper" style={{ '--program-accent': program.accentColor }}>
            <div className="monthly-program-header">
              <div className="monthly-program-brand">
                <img
                  src={logoSource}
                  alt={selectedCenter?.name || 'Logo du centre'}
                />
                <div>
                  <div className="monthly-program-brand-name">{displayCenterName}</div>
                  {selectedCenter?.address ? <div className="monthly-program-brand-address">{selectedCenter.address}</div> : null}
                </div>
              </div>
              <div className="monthly-program-heading">
                <h1>{displayScheduleTitle}</h1>
                <p>{displayProgramSubtitle}</p>
              </div>
            </div>

            <div className="monthly-program-hero-row">
              <div className="monthly-program-hero-card">
                <div className="monthly-program-hero-script">{program.heroTitle || 'Temps forts du mois'}</div>
                <span className="monthly-program-hero-label">{program.subtitle || 'Activites, repas et moments forts du mois'}</span>
                <p>{program.heroDescription || 'Ajoutez un message d accueil, une fete du mois ou une note pour les familles.'}</p>
                <div className="monthly-program-hero-art" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
              </div>
              <div className={`monthly-program-season-card is-${monthTheme.variant}`}>
                <div className="monthly-program-season-text">
                  <span>{displaySeasonTitle}</span>
                  <strong>{displaySeasonHeading}</strong>
                  <p>{displaySeasonDescription}</p>
                </div>
                <div className="monthly-program-season-art" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            </div>

            <div className="monthly-program-weekday-header">
              {WEEKDAY_KEYS.map((weekday) => (
                <div key={weekday}>{weekdayLabel(weekday, locale)}</div>
              ))}
            </div>

            <div className="monthly-program-grid">
              {weeks.map((week, weekIndex) => (
                <React.Fragment key={`week-${weekIndex}`}>
                  {weekIndex < weeks.length - 1 ? (
                    <div className="monthly-program-week-action" style={{ gridColumn: '1 / -1' }}>
                      {canEdit ? (
                        <button type="button" className="btn btn-outline btn-sm" onClick={() => copyWeekForward(weekIndex)}>
                          Dupliquer cette semaine vers la suivante
                        </button>
                      ) : (
                        <span />
                      )}
                    </div>
                  ) : null}
                  {week.map((date) => {
                    const dayKey = getDateKey(date);
                    const dayData = normalizeDay(program.days?.[dayKey] || DEFAULT_DAY);
                    const syncedActivities = scheduledActivitiesByDay[dayKey] || [];
                    const inMonth = isSameMonth(date, selectedMonth);
                    const isFutureOrToday = !isBefore(startOfDay(date), today);
                    const canEditDay = canEdit && (inMonth || isFutureOrToday);

                    return (
                      <button
                        key={dayKey}
                        type="button"
                        className={`monthly-program-cell${inMonth ? '' : ' is-outside'}${!inMonth && isFutureOrToday ? ' is-upcoming' : ''}${canEditDay ? ' is-clickable' : ''}`}
                        onClick={() => canEditDay && openDayEditor(date)}
                        disabled={!canEditDay}
                      >
                        <div className="monthly-program-cell-head">
                          <span>{format(date, 'EEEE', { locale })}</span>
                          <strong>{format(date, 'd')}</strong>
                        </div>
                        <div className="monthly-program-entry-list">
                          {syncedActivities.length ? (
                            <div>
                              <div className="monthly-program-entry-section monthly-program-entry-section-synced">Activites planifiees</div>
                              {syncedActivities.map((activity) => (
                                <div key={activity.id} className="monthly-program-entry monthly-program-entry-synced">
                                  <span>{activity.starts_at ? format(new Date(activity.starts_at), 'HH:mm') : '-'}</span>
                                  <span>{activity.title}</span>
                                </div>
                              ))}
                            </div>
                          ) : null}
                          {dayData.sections.some((section) => section.title || section.entries.length) ? dayData.sections.map((section, sectionIndex) => (
                            <div key={`${dayKey}-section-${sectionIndex}`}>
                              {section.title ? <div className="monthly-program-entry-section">{section.title}</div> : null}
                              {section.entries.map((entry, entryIndex) => (
                                <div key={`${dayKey}-section-${sectionIndex}-entry-${entryIndex}`} className="monthly-program-entry">
                                  <span>{entry.time || '-'}</span>
                                  <span>{entry.text}</span>
                                </div>
                              ))}
                            </div>
                          )) : (inMonth ? <div className="monthly-program-empty">Cliquer pour ajouter le contenu du jour</div> : null)}
                        </div>
                        {dayData.note ? <div className="monthly-program-note">{dayData.note}</div> : null}
                      </button>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>

            <div className="monthly-program-footer">
              <div>{displayFooter}</div>
              <div className="monthly-program-decoration" aria-hidden="true">
                <span />
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>
        )}
      </div>

      {editorDate && program ? (
        <DayEditorModal
          date={editorDate}
          value={program.days?.[getDateKey(editorDate)] || DEFAULT_DAY}
          onClose={() => setEditorDate(null)}
          onSave={saveDay}
        />
      ) : null}
    </div>
  );
};

export default MonthlyProgramPage;
